import { i18n } from 'i18n';
import {
  FillType,
  HydraulicBackfillSpecificationDto,
  HydraulicRecipeSpecificationDto,
  PasteBackfillSpecificationDto,
  PasteRecipeSpecificationDto,
  PourReportDto,
  RheologyDataDto,
  StopeDocument,
  ThroughputControlType
} from 'providers/api';
import { generateMassBalanceFromFlowRate } from 'utils/MassBalance/flowrate';
import { generateMassBalanceFromTonnage } from 'utils/MassBalance/tonnage';

// The reports page only ever needs these fields off a stope, so widening to this instead of StopeDataDto
// lets a placeholder stand in for a stope that no longer exists without fabricating required fields.
export interface ReportStope {
  stopeId: string;
  stopeName: string;
  volume?: number;
  documents?: StopeDocument[];
}

// A pour whose stopeId is no longer in the mine model's reticulation data (model re-imported, stope
// removed) was being dropped from the reports page entirely — the same silent-loss pattern as the
// IsRockFill NULLs. Surface it under a placeholder stope instead, mirroring the "Unknown Stope"
// fallback that GetUcsStrengthGraphData and the Stope Pour Summary already use. The id is kept in the
// label so several orphans stay distinguishable and the stope is traceable back to the data.
export const appendOrphanedStopes = (
  stopes: ReportStope[],
  poursByStopeId: Record<string, PourReportDto[] | undefined>,
): ReportStope[] => {
  const known = new Set(stopes.map(({ stopeId }) => stopeId));
  const orphans = Object.keys(poursByStopeId)
    .filter((stopeId) => !known.has(stopeId) && (poursByStopeId[stopeId] ?? []).length > 0)
    .map((stopeId) => ({
      stopeId,
      stopeName: `${i18n._('Unknown stope')} (${stopeId})`,
      documents: [],
    }));

  return [...stopes, ...orphans];
};

export interface PourMetrics {
  massConcentration?: number;
  binderPercent?: number;
  volume: number;
  dryTonnes?: number;
  binderTonnes?: number;
  durationHours?: number;
  ucs?: number;
}

const ratioToBinderPercent = (ratio?: number): number | undefined => (ratio === undefined || ratio === null
  ? undefined
  : (ratio / (1 + ratio)) * 100);

// What the plant actually placed, read straight off the pour's completion data. Nothing here is
// modelled — a field stays undefined when the operator did not record it, so the report shows "-"
// rather than implying a figure.
//
// Deliberately omits PourData.dryTonnage: it is captured as a feed *rate* (the form labels it
// "Dry Tonnage (t/h)"), so it is not the same quantity as the design dry tonnes placed and must not
// be presented alongside it.
export interface ActualPourMetrics {
  massConcentration?: number;
  binderPercent?: number;
  volume?: number;
  binderTonnes?: number;
  ucs?: number;
}

export const computeActualPourMetrics = (pour: PourReportDto, fillType: FillType): ActualPourMetrics => {
  const data = pour.pourData;
  if (!data) return {};

  return {
    massConcentration: data.massConcentration,
    binderPercent: fillType === FillType.Paste
      ? data.binderContent
      : ratioToBinderPercent(data.binderTailingsRatio),
    volume: data.volume,
    binderTonnes: data.totalBinder,
    ucs: data.ucsAverage,
  };
};

export type PourMetricKey = 'dryTonnes' | 'volume' | 'binderTonnes' | 'massConcentration' | 'ucs';

const getMassBalance = (
  throughput: number,
  controlType: ThroughputControlType,
  massConcentrationPct: number,
  binderConcentrationPct: number,
  rheology: RheologyDataDto,
) => {
  const common = {
    binderConcentration: binderConcentrationPct,
    totalDryConcentrationByMass: massConcentrationPct,
    tailingsSolidsDensity: rheology.tailingsDrySolidsDensity,
    binderParticleDensity: rheology.binderDryParticleDensity,
    carrierFluidDensity: rheology.carrierFluidDensity,
  };

  if (controlType === ThroughputControlType.FlowRate) {
    return generateMassBalanceFromFlowRate({ totalFlowRate: throughput, ...common });
  }

  return generateMassBalanceFromTonnage({
    totalTonnage: throughput,
    isDryTonnage: controlType === ThroughputControlType.DryTonnage,
    ...common,
  });
};

/**
 * The specification fields the throughput figures are read from. `BackfillRecipeDto` and `PourReportDto`
 * both satisfy it structurally, which is the point: a recipe being *considered* on the create-pour form
 * and a pour already committed must produce the same duration, so they resolve it through one function
 * rather than two that drift.
 */
export interface PourThroughputSource {
  pasteSpecification?: PasteBackfillSpecificationDto | undefined;
  pasteSelectedSpecification?: PasteRecipeSpecificationDto | undefined;
  pasteOptimisedSpecification?: PasteRecipeSpecificationDto | undefined;
  hydraulicSpecification?: HydraulicBackfillSpecificationDto | undefined;
  hydraulicSelectedSpecification?: HydraulicRecipeSpecificationDto | undefined;
  hydraulicOptimisedSpecification?: HydraulicRecipeSpecificationDto | undefined;
}

interface ResolvedThroughput {
  massConcentration: number;
  binderPercent: number;
  slurryDensity: number;
  totalFlowRate: number;
}

// The selected recipe wins, falling back to the optimised one; the design specification carries the
// throughput. Returns undefined when any input is missing, so callers show "-" rather than a figure
// derived from a default.
const resolveThroughput = (
  source: PourThroughputSource,
  rheology: RheologyDataDto | undefined,
  fillType: FillType,
): ResolvedThroughput | undefined => {
  const isPaste = fillType === FillType.Paste;
  const recipeSpec = isPaste
    ? (source.pasteSelectedSpecification ?? source.pasteOptimisedSpecification)
    : (source.hydraulicSelectedSpecification ?? source.hydraulicOptimisedSpecification);
  const backfillSpec = isPaste ? source.pasteSpecification : source.hydraulicSpecification;

  const massConcentration = recipeSpec?.massConcentration;
  const binderPercent = isPaste
    ? (recipeSpec as PasteRecipeSpecificationDto | undefined)?.binderContent
    : ratioToBinderPercent((backfillSpec as HydraulicBackfillSpecificationDto | undefined)?.binderTailingsRatio);

  if (!rheology || !backfillSpec?.throughput || backfillSpec.throughputControlType === undefined
    || massConcentration === undefined || binderPercent === undefined) {
    return undefined;
  }

  const massBalance = getMassBalance(
    backfillSpec.throughput,
    backfillSpec.throughputControlType,
    massConcentration,
    binderPercent,
    rheology,
  );

  return {
    massConcentration,
    binderPercent,
    slurryDensity: massBalance.slurryDensity,
    totalFlowRate: massBalance.totalFlowRate,
  };
};

/**
 * The volumetric flow rate a pour is placed at, m3/h. Undefined whenever the recipe or rheology cannot
 * supply one — including rock fill, which has no recipe at all, so callers must not substitute a
 * fallback: a rise rate drawn from a default flow rate would look like a measurement.
 */
export const computePourFlowRate = (
  source: PourThroughputSource,
  rheology: RheologyDataDto | undefined,
  fillType: FillType,
): number | undefined => {
  const throughput = resolveThroughput(source, rheology, fillType);
  return throughput && throughput.totalFlowRate > 0 ? throughput.totalFlowRate : undefined;
};

/**
 * How long `volume` takes to place. Defers to computePourFlowRate so the duration quoted for a pour and
 * the rate its rise-rate graph is drawn at can never disagree.
 */
export const computePourDurationHours = (
  volume: number,
  source: PourThroughputSource,
  rheology: RheologyDataDto | undefined,
  fillType: FillType,
): number | undefined => {
  const flowRate = computePourFlowRate(source, rheology, fillType);
  return flowRate === undefined ? undefined : volume / flowRate;
};

// Derives the per-pour figures for the pour report from the selected recipe (falling back to the
// optimised/design recipe) and the project rheology. Dry tonnes mirror the backend pour-note formula
// (volume x slurry density x mass concentration) so they reconcile with CalculatedBinderTonnes, and
// the pour duration is the volume divided by the volumetric flow rate of the throughput.
export const computePourMetrics = (
  pour: PourReportDto,
  rheology: RheologyDataDto | undefined,
  fillType: FillType,
): PourMetrics => {
  const volume = pour.volume ?? 0;
  const isPaste = fillType === FillType.Paste;

  const recipeSpec = isPaste
    ? (pour.pasteSelectedSpecification ?? pour.pasteOptimisedSpecification)
    : (pour.hydraulicSelectedSpecification ?? pour.hydraulicOptimisedSpecification);
  const backfillSpec = isPaste ? pour.pasteSpecification : pour.hydraulicSpecification;

  const massConcentration = recipeSpec?.massConcentration;
  const binderPercent = isPaste
    ? (recipeSpec as PasteRecipeSpecificationDto | undefined)?.binderContent
    : ratioToBinderPercent((backfillSpec as HydraulicBackfillSpecificationDto | undefined)?.binderTailingsRatio);

  // Hydraulic recipes carry no UCS figure, so the design UCS column stays blank for them.
  const ucs = isPaste ? (recipeSpec as PasteRecipeSpecificationDto | undefined)?.ucsStrength : undefined;

  const base: PourMetrics = { volume, massConcentration, binderPercent, ucs };
  const throughput = resolveThroughput(pour, rheology, fillType);

  if (!throughput) {
    return { ...base, binderTonnes: pour.calculatedBinderTonnes ?? undefined };
  }

  const dryTonnes = volume * (throughput.slurryDensity / 1000) * (throughput.massConcentration / 100);
  const binderTonnes = pour.calculatedBinderTonnes ?? dryTonnes * (throughput.binderPercent / 100);

  return {
    ...base,
    dryTonnes,
    binderTonnes,
    durationHours: computePourDurationHours(volume, pour, rheology, fillType),
  };
};
